// Owner decisions on a due hour-raise alert (docs/payroll/app-hour-raise-
// decisions.md, owner 2026-09-08). Shared by RaiseAlerts.tsx (Home) and
// ManagerEntry.tsx (the "Hour raises" section a manager — or the owner,
// filling in for one — actually works from), so the request shape and the
// muted caption are built in exactly one place.
import { authedWrite } from './apiClient';
import { payPeriodLabel, type LabelledPayPeriod } from './payPeriodLabel';
import { formatDate } from './date';

export type RaiseDecisionKind = 'REJECTED' | 'DEFERRED';

/** What GET /api/v1/hour-raises attaches to a raise that still shows despite
 * a past decision — a DEFERRED one arriving at its target period, or a
 * REJECTED one that crossed its next 1,000-hour threshold. */
export interface RaiseDecisionInfo {
  decision: RaiseDecisionKind;
  payPeriodId: number;
  deferToPeriodId: number | null;
  reason: string;
  decidedBy: string;
  decidedAt: string;
}

export interface DecideHourRaiseInput {
  employeeId: number;
  payPeriodId: number;
  decision: RaiseDecisionKind;
  deferToPeriodId?: number;
  reason: string;
}

export interface DecideHourRaiseResult {
  decision: RaiseDecisionInfo & { id: number; employeeId: number };
  version: number;
}

/** OWNER only — the server refuses anyone else with 403 (R-owner-decides). */
export function decideHourRaise(
  input: DecideHourRaiseInput,
  key: string = crypto.randomUUID(),
): Promise<DecideHourRaiseResult> {
  return authedWrite<DecideHourRaiseResult>('POST', '/api/v1/hour-raises/decide', input, key);
}

/**
 * The muted line under a raise that still shows despite a past decision.
 * "Moved to Week NN" reads the ACTUAL following period label (R-10: no bare
 * "Week N" is ever assembled by hand) — passed in because this module does
 * not know which period is "following" for the raise being rendered.
 *
 * `clinic` picks that raise's own employee's clinic out of `following`'s
 * `datesByCompany` (Lake Mary runs one day later than the other three —
 * docs/payroll/app-period-dates-per-clinic.md); omitted, this falls back to
 * `following`'s own shared dates, same as `payPeriodLabel` always has.
 */
export function decisionCaption(
  decision: RaiseDecisionInfo | null | undefined,
  following: LabelledPayPeriod | null,
  clinic?: string | null,
): string | null {
  if (!decision) return null;
  if (decision.decision === 'REJECTED') {
    const why = decision.reason ? ` — ${decision.reason}` : '';
    return `Rejected by ${decision.decidedBy} on ${formatDate(decision.decidedAt)}${why} — due again`;
  }
  const label = following ? payPeriodLabel(following, clinic) : 'a later pay period';
  return `Moved to ${label} by ${decision.decidedBy} on ${formatDate(decision.decidedAt)}`;
}
